import { memo, useCallback, useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Activity, AlertTriangle, CheckCircle, Database, XCircle } from 'lucide-react';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';
import { starchartApiService } from '../../services/starchartApi';
import { useVisiblePolling } from '../../hooks/useVisiblePolling';
import { apiClockSkewSeconds } from '../../utils/starchart';
import type { StarchartDbInfoResponse, StarchartHealthResponse } from '../../types/starchart';

const POLL_MS = 30_000;
/** Beyond this the API's clock and ours disagree enough to distrust timestamps. */
const SKEW_WARN_SECONDS = 30;

interface ApiHealthCardProps {
  dbInfo: StarchartDbInfoResponse | null;
}

const Fact = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="subcard p-3">
    <div className="eyebrow">{label}</div>
    <div className="text-sm font-medium text-white tabular-nums mt-0.5 truncate">{value}</div>
  </div>
);

/**
 * StarChart service status.
 *
 * Deliberately does not show an uptime. The API's `uptime_seconds` field is neither an uptime nor
 * a start time — it is the service's own current clock, so subtracting it from now always yields
 * zero. The standalone site rendered it as a duration and therefore always displayed "0m". What
 * it can honestly tell us is clock skew, and how fresh the stored data is, which are both more
 * useful anyway.
 */
export const ApiHealthCard = memo(({ dbInfo }: ApiHealthCardProps) => {
  const [health, setHealth] = useState<StarchartHealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    starchartApiService
      .getHealth()
      .then((response) => {
        setHealth(response);
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Health check failed'));
  }, []);

  useEffect(load, [load]);
  useVisiblePolling(load, POLL_MS);

  const skew = health ? apiClockSkewSeconds(health) : null;
  const skewed = skew !== null && Math.abs(skew) > SKEW_WARN_SECONDS;
  const latest = dbInfo?.latest_timestamp ? new Date(dbInfo.latest_timestamp * 1000) : null;

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader
        title="StarChart service"
        icon={Activity}
        actions={
          error ? (
            <span className="badge badge-danger badge-dot">Unreachable</span>
          ) : health ? (
            <span
              className={cn('badge badge-dot', skewed ? 'badge-warning' : 'badge-success')}
            >
              {health.status}
            </span>
          ) : null
        }
      />

      {error ? (
        <div className="alert alert-danger mt-3">
          <XCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <Fact
              label="Data freshness"
              value={latest ? formatDistanceToNow(latest, { addSuffix: true }) : '—'}
            />
            <Fact
              label="Stored samples"
              value={(dbInfo?.total_records ?? health?.db_total_records ?? 0).toLocaleString()}
            />
            <Fact label="Active sessions" value={(health?.active_sessions_count ?? 0).toLocaleString()} />
            <Fact label="Redis keys" value={(health?.redis_keys_count ?? 0).toLocaleString()} />
          </div>

          <div className="alert mt-3" role="status">
            {skewed ? (
              <AlertTriangle className="w-4 h-4 shrink-0 text-yellow-400" />
            ) : (
              <CheckCircle className="w-4 h-4 shrink-0 text-green-400" />
            )}
            <span className="text-xs">
              {skew === null
                ? 'Checking the service clock…'
                : skewed
                  ? `The service clock is ${Math.abs(skew)}s ${skew > 0 ? 'behind' : 'ahead of'} yours, so timestamps may be slightly off.`
                  : 'Service clock is in sync with yours.'}
            </span>
          </div>

          {dbInfo?.earliest_timestamp && (
            <p className="text-xs text-glass-500 mt-3 flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5 shrink-0" />
              Raw samples go back to{' '}
              {formatDistanceToNow(new Date(dbInfo.earliest_timestamp * 1000), { addSuffix: true })}.
              Aggregated data reaches back much further — a full year at hourly resolution.
            </p>
          )}
        </>
      )}
    </div>
  );
});

ApiHealthCard.displayName = 'ApiHealthCard';
